import React, { useState, useEffect, useRef } from 'react';
import { WalletValue, walletAmount, useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Users, History, ShieldCheck, Timer, Trophy, Rocket, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useChatLayout } from '../context/ChatLayoutContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');
const API = `${BACKEND_URL}/api`;

// ==========================================
// 🛡️ API SPAM KORUMALI AVATAR BİLEŞENİ
// Hata veren resimleri hafızaya alır, backend'i saniyede 100 kere yormaz!
// ==========================================
const failedAvatars = new Set();

const Avatar = React.memo(({ username, className }) => {
  const [hasError, setHasError] = useState(failedAvatars.has(username));
  const src = hasError 
    ? "/default-avatar.png" 
    : `${API}/user/avatar/${username}`;

  return (
    <img 
      src={src} 
      alt={`${username} Avatar`} 
      className={className}
      onError={() => {
        failedAvatars.add(username);
        setHasError(true);
      }}
      loading="lazy"
    />
  );
});

// ==========================================
// K, M, B FORMATLAYICILARI
// ==========================================
const parseAmount = (valStr) => {
  if (!valStr) return 0;
  // ⚠️ BİNLİK AYIRICI TEMİZLENMELİ. formatAmount() büyük sayıları
  // toLocaleString ile üretiyor ("1,457,545"); parseFloat virgülde durup
  // 1 döndürüyordu. MAX butonuna basan kullanıcı 1 birim bahis oynuyordu.
  let str = valStr.toString().trim().toLowerCase().replace(/[,\s]/g, '');
  let multiplier = 1;
  if (str.endsWith('k')) { multiplier = 1000; str = str.slice(0, -1); }
  else if (str.endsWith('m')) { multiplier = 1000000; str = str.slice(0, -1); }
  else if (str.endsWith('b')) { multiplier = 1000000000; str = str.slice(0, -1); }
  
  const parsedValue = parseFloat(str);
  return isNaN(parsedValue) ? 0 : Math.floor(parsedValue * multiplier);
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '0';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

const SLICE_COLORS = [
    '#ec4899', '#3b82f6', '#10b981', '#f59e0b', 
    '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#6366f1'
];

export const Jackpot = () => {
  const { user, token, isAuthenticated, balance, fmtMoney, wallet, betToRaw, rawToBet, betLimits } = useAuth();
  
  // Referanslar
  const wsRef = useRef(null);
  const spinTimeoutRef = useRef(null);
  
  // Bahis Durumları
  const [betAmount, setBetAmount] = useState('');
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [isJoining, setIsJoining] = useState(false);
  
  // Oyun Durumları (State)
  const [phase, setPhase] = useState('waiting'); 
  const [timeRemaining, setTimeRemaining] = useState(0); 
  const [players, setPlayers] = useState([]); 
  const [totalPot, setTotalPot] = useState(0);
  // 🎰 Turun MÜHÜRLÜ cüzdanı — tüm tutarlar bununla biçimlendirilir.
  const [roundWallet, setRoundWallet] = useState(null);
  const fmtRound = (v) => walletAmount(v, roundWallet);
  const [history, setHistory] = useState([]);
  const [serverHash, setServerHash] = useState('');
  const [revealedSeed, setRevealedSeed] = useState('');
  
  // Animasyon Durumları
  const [spinDegree, setSpinDegree] = useState(0);
  const [winnerInfo, setWinnerInfo] = useState(null);
  const [showWinner, setShowWinner] = useState(false);

  const SPIN_DURATION = 6; // Çarkın dönme süresi (Saniye)

  // Kendi bahis hesaplamalarımız
  const myBet = players.find(p => p.user_id === user?.id);
  const myChance = totalPot > 0 && myBet ? ((myBet.amount / totalPot) * 100).toFixed(2) : "0.00";

  // 🚀 ZIRH: React durumlarını okumak için referans hafızası
  const stateRef = useRef({ players, totalPot, winnerInfo, phase, showWinner });
  useEffect(() => {
    stateRef.current = { players, totalPot, winnerInfo, phase, showWinner };
  }, [players, totalPot, winnerInfo, phase, showWinner]);

  // Bileşen kapanırken memory leak olmaması için temizleme
  useEffect(() => {
    return () => {
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
    };
  }, []);

  // ==========================================
  // 1-1 GERİ SAYIM İÇİN LOKAL TİMER
  // ==========================================
  useEffect(() => {
      const interval = setInterval(() => {
          setTimeRemaining(prev => {
              if (prev > 0 && stateRef.current.phase !== 'spinning') {
                  return prev - 1;
              }
              return prev;
          });
      }, 1000);
      return () => clearInterval(interval);
  }, []);

  // ==========================================
  // WEBSOCKET BAĞLANTISI (Kopmaz & Titremez)
  // ==========================================
  useEffect(() => {
    const wsToken = token || 'guest';
    const ws = new WebSocket(`${WS_URL}/ws/jackpot/${wsToken}`);
    wsRef.current = ws;

    ws.onopen = () => console.log('🚀 Connected to Jackpot Server!');

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'JACKPOT_STATE') {
          setPhase(data.phase);
          setTimeRemaining(data.time);
          setPlayers(data.players || []);
          setTotalPot(data.total_pot || 0);
          if (data.wallet) setRoundWallet(data.wallet);
          
          if (data.server_seed_hash) setServerHash(data.server_seed_hash);
          if (data.history) setHistory(data.history);
          
          if (data.phase !== 'spinning') {
              setSpinDegree(0);
              setRevealedSeed('');
              // Sadece ekranda hali hazırda kazanan gösterilmiyorsa temizle
              // (Böylece 4 saniyelik gecikme sırasında rastgele bir state gelirse yazı kaybolmaz)
              if (!stateRef.current.showWinner) {
                  setWinnerInfo(null);
                  setShowWinner(false);
              }
          }
        }
        else if (data.type === 'JACKPOT_UPDATE') {
          setPlayers(data.players);
          setTotalPot(data.total_pot);
          if (data.wallet) setRoundWallet(data.wallet);
        }
        else if (data.type === 'JACKPOT_TICK') {
          setPhase('countdown');
          // Lokal sayacın 1-1 temiz azalmasını bozmamak için sadece 
          // arka uç ile aradaki fark > 1.5 ise backend'den veriyi zorla.
          setTimeRemaining(prev => {
              if (Math.abs(prev - data.time) > 1.5) return data.time;
              return prev;
          });
        }
        else if (data.type === 'JACKPOT_SPINNING') {
          setPhase('spinning');
          setTimeRemaining(data.time);
          setRevealedSeed(data.server_seed);
          
          const currentTotalPot = stateRef.current.totalPot || data.total_pot || 1;
          const currentPlayers = stateRef.current.players || [];
          
          // 🎯 MATEMATİKSEL KUSURSUZ OK DÖNÜŞÜ
          const ratio = data.winning_ticket / currentTotalPot;
          const targetAngle = ratio * 360; 
          // Daha heyecanlı dönüş: 10 tur yerine 15 tur
          const totalRotation = (360 * 15) + targetAngle; 
          
          setSpinDegree(totalRotation);
          
          const winnerPlayer = currentPlayers.find(p => p.user_id === data.winner_id);
          setWinnerInfo({
              username: data.winner_username,
              chance: ((winnerPlayer?.amount || 0) / currentTotalPot * 100).toFixed(2),
              winAmount: currentTotalPot * 0.95,
              avatar: winnerPlayer?.roblox_username || data.winner_username
          });

          // Animasyon heyecanı için kazananı çark durduğunda göster
          setShowWinner(false);
          if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
          
          spinTimeoutRef.current = setTimeout(() => {
              setShowWinner(true);
          }, SPIN_DURATION * 1000);
        }
        else if (data.type === 'JACKPOT_RESET') {
          setHistory(prev => {
              const wInfo = stateRef.current.winnerInfo;
              if (wInfo) {
                  return [{ winner: wInfo.username, pot: stateRef.current.totalPot, avatar: wInfo.avatar }, ...prev].slice(0, 10);
              }
              return prev;
          });
          
          if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
          
          setPhase('waiting');
          setTimeRemaining(0);
          setPlayers([]);
          setTotalPot(0);
          setRoundWallet(null);   // yeni tur kendi cüzdanını bildirecek
          setSpinDegree(0);
          setRevealedSeed('');
          
          if (data.server_seed_hash) setServerHash(data.server_seed_hash);

          // Kazanan yazısının "biraz kalması" için ekran temizlemeyi geciktiriyoruz
          setTimeout(() => {
              setShowWinner(false);
              setWinnerInfo(null);
          }, 1000);
        }
        else if (data.type === 'ERROR') {
            toast.error(data.message);
        }

      } catch (error) {
        console.error("WS Parse Error:", error);
      }
    };

    return () => ws.close();
  }, [token]); 

  // ==========================================
  // BAHİS (JOIN) İŞLEMİ
  // ==========================================
  const joinJackpot = async () => {
    if (!isAuthenticated) return toast.error("Please log in first!");
    
// 🎯 Aktif cüzdanın biriminde girilir → saklama birimine çevrilir.
    const amount = betToRaw(parseAmount(betAmount));

// ⚠️ Jackpot'un KENDİ sınırları var ve cüzdana göre değişir
    // (site 1–100.000, BB 1.000.000–200.000.000 mili-BB). Gömülü
    // "100 – 20.000" site birimiydi ve BB bahislerini reddederdi.
    if (!amount || amount < betLimits.jackpot_min || amount > betLimits.jackpot_max) {
      return toast.error(`Bet must be between ${walletAmount(betLimits.jackpot_min, wallet)} and ${walletAmount(betLimits.jackpot_max, wallet)}.`);
    }
    // 🍀 AKTİF CÜZDANIN bakiyesiyle karşılaştır (LC'de mili-LC).
    if (amount > balance) return toast.error("Insufficient balance!");
    if (phase === 'spinning') return toast.error("The wheel is spinning! Wait for the next round.");

    setIsJoining(true);
    try {
        await axios.post(`${API}/game/jackpot/join`, { amount }, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success("Successfully joined the Jackpot!");
        setBetAmount('');
    } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to join jackpot!");
    } finally {
        setIsJoining(false);
    }
  };

  // ==========================================
  // ÇARK (WHEEL) RENDER İŞLEMİ
  // ==========================================
  const renderWheel = () => {
    const radius = 140;
    const circumference = 2 * Math.PI * radius;
    let currentOffset = 0;

    const slices = players.map((p, idx) => {
        const ratio = totalPot > 0 ? (p.amount / totalPot) : 0; 
        const dash = ratio * circumference;
        const offset = currentOffset;
        currentOffset += dash;
        
        return {
            ...p,
            dash: dash,
            offset: offset,
            color: SLICE_COLORS[idx % SLICE_COLORS.length]
        };
    });

    return (
        <div className="relative w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[340px] md:h-[340px] lg:w-[400px] lg:h-[400px] flex items-center justify-center mt-2 shrink-0">
            
            {/* 🚀 DÖNEN OK (SADECE OK DÖNER - ÇARK SABİT) */}
            <motion.div 
                className="absolute inset-0 z-20 pointer-events-none"
                animate={{ rotate: spinDegree }}
                transition={{ 
                    duration: spinDegree > 0 ? SPIN_DURATION : 0, 
                    // [0.1, 0.0, 0.05, 1] -> Hızlıca hızlanır, yavaşlarken sürüklenir (drift)
                    ease: [0.15, 0.0, 0.0, 1]
                }}
                style={{ originX: 0.5, originY: 0.5 }}
            >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 flex flex-col items-center drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]">
                   <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-b from-[#B059E8] to-[#7b2fc4] clip-triangle shadow-lg"></div>
                   <style>{`.clip-triangle { clip-path: polygon(50% 100%, 0 0, 100% 0); }`}</style>
                </div>
            </motion.div>

            {/* 🛑 SABİT ÇARK (WHEEL) */}
            <div className="w-full h-full relative">
                <svg viewBox="0 0 320 320" className="w-full h-full transform -rotate-90">
                    {slices.length === 0 && (
                        <circle cx="160" cy="160" r={radius} fill="transparent" stroke="#2A2D31" strokeWidth="24" opacity="0.3" />
                    )}
                    
                    {slices.map((slice) => (
                        <circle
                            key={slice.user_id}
                            cx="160" cy="160" r={radius}
                            fill="transparent"
                            stroke={slice.color}
                            strokeWidth="24"
                            strokeDasharray={`${slice.dash} ${circumference}`}
                            strokeDashoffset={-slice.offset}
                            className="transition-all duration-300 ease-out"
                        />
                    ))}
                </svg>
            </div>

            {/* ÇARK ORTASI (BİLGİ & KAZANAN EKRANI) */}
            <div className="absolute inset-0 m-auto w-[200px] h-[200px] sm:w-[240px] sm:h-[240px] md:w-[260px] md:h-[260px] lg:w-[310px] lg:h-[310px] bg-[#1A1D20] rounded-full border-4 border-[#0E0F11] shadow-[inset_0_0_40px_rgba(0,0,0,0.8)] flex flex-col items-center justify-center z-10 p-4 text-center">
                
                {/* EĞER KAZANAN EKRANI GÖSTERİLECEKSE (Background phase'den bağımsız) */}
                {showWinner && winnerInfo ? (
                    <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center w-full px-2">
                        <Trophy className="w-8 h-8 md:w-12 md:h-12 text-yellow-400 mb-1 drop-shadow-[0_0_15px_rgba(250,204,21,0.6)]" />
                        <h3 className="text-zinc-400 text-[10px] md:text-xs font-bold uppercase tracking-widest">Congratulations</h3>
                        <p className="text-lg md:text-2xl font-black text-white truncate w-full">{winnerInfo.username} won!</p>
                        <p className="text-[#2ecf6e] mm-num font-bold text-base md:text-xl mt-0.5">{/* 🍀 Jackpot TURU TEK CÜZDANLIDIR (backend D4): havuz ve ödül
                            turun mühürlü cüzdanının biriminde. */}
                        +<WalletValue amount={winnerInfo.winAmount} wallet={roundWallet} /></p>
                        <p className="text-zinc-500 text-[9px] md:text-xs mt-0.5">with {winnerInfo.chance}% chance</p>
                    </motion.div>
                ) : (
                    /* NORMAL BEKLEME EKRANI VEYA ÇARK DÖNÜYORKEN Kİ EKRAN */
                    <>
                        <h3 className="text-zinc-500 text-xs md:text-sm font-bold uppercase tracking-widest mb-1">Total Pot</h3>
                        <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] mm-num">
                            {/* ⚠️ Sunucudan HAM birimde gelir; fmtMoney sembolü de kendi ekler. */}
                            <WalletValue amount={totalPot} wallet={roundWallet} />
                        </p>

                        <div className="mt-3 md:mt-6 flex flex-col items-center">
                            <p className="text-zinc-500 text-[10px] md:text-xs font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                                {phase === 'spinning' ? <Rocket className="w-3 h-3 text-[#B059E8]" /> : <Timer className="w-3 h-3" />}
                                {phase === 'spinning' ? 'Rolling' : 'Timer'}
                            </p>
                            
                            <p className={`text-xl md:text-2xl lg:text-3xl font-black mm-num tracking-widest 
                                ${phase === 'waiting' ? 'text-zinc-600' : 
                                 (phase === 'spinning' ? 'text-[#B059E8] animate-pulse' : 
                                 (timeRemaining <= 10 ? 'text-rose-500 animate-pulse' : 'text-zinc-300'))}`}
                            >
                                {phase === 'waiting' ? '00:00' : 
                                (phase === 'spinning' ? 'GL!' : 
                                `${Math.floor(timeRemaining / 60).toString().padStart(2, '0')}:${(timeRemaining % 60).toString().padStart(2, '0')}`)}
                            </p>
                        </div>
                        
                        <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-[#2A2D31]/50 w-full flex justify-center px-4">
                            <div className="flex flex-col items-center">
                                <span className="text-[9px] md:text-[10px] text-zinc-500 uppercase font-bold tracking-widest">Your Chance</span>
                                <span className="text-[#2ecf6e] mm-num font-bold text-xs md:text-sm">{myChance}%</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

        </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0E0F11] pt-24 pb-8" data-testid="jackpot-page">
      <div className="max-w-[1800px] mx-auto px-4">
        <div className={`flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          
          {/* SOL BÖLÜM: CANLI SOHBET */}
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          {/* SAĞ BÖLÜM: OYUN ALANI (Taşmayı engelleyen min-w-0 kalkanı) */}
          <main className="flex-1 flex flex-col xl:grid xl:grid-cols-[1fr_360px] gap-6 min-w-0">
            
            {/* 1. KISIM: ÇARK VE GEÇMİŞ */}
            <div className="flex flex-col gap-6 min-w-0">
                
                {/* GEÇMİŞ BARI (HISTORY) */}
                <div className="bg-[#1A1D20] rounded-xl border border-[#2A2D31] p-3 flex items-center gap-3 overflow-x-auto scrollbar-none shadow-lg w-full">
                    <History className="w-5 h-5 text-zinc-500 mr-1 flex-shrink-0" />
                    {history.length === 0 ? (
                        <span className="text-zinc-600 text-xs mm-num">No recent games...</span>
                    ) : (
                        history.map((game, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-[#15171a] border border-[#2A2D31] rounded-full px-3 py-1 shrink-0 group cursor-pointer hover:border-[#B059E8]/50 transition-colors">
                                {/* 🛡️ DOĞRUDAN API İLE AVATAR */}
                                <Avatar 
                                    username={game.winner} 
                                    className="w-5 h-5 rounded-full object-cover border border-[#2A2D31] bg-[#15171a]"
                                />
                                <span className="text-xs text-white font-bold max-w-[80px] truncate">{game.winner}</span>
                                <span className="text-xs text-[#2ecf6e] mm-num font-bold ml-1"><WalletValue amount={game.pot} wallet={game.wallet} /></span>
                            </div>
                        ))
                    )}
                </div>

                {/* ÇARK KUTUSU */}
                <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-2xl p-4 md:p-8 flex flex-col items-center justify-center relative overflow-hidden w-full">
                    {/* Arka Plan Işıklandırması */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-[#B059E8]/5 rounded-full blur-[80px] pointer-events-none"></div>
                    
                    {renderWheel()}

                    <div className="mt-6 md:absolute md:bottom-4 md:left-4 flex items-center gap-3 text-xs text-zinc-600 mm-num z-10 bg-[#15171a]/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-[#2A2D31]/50 w-full md:w-auto justify-center md:justify-start">
                        <div className="flex items-center gap-1">
                            <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                            <span>Provably Fair</span>
                        </div>
                        {serverHash && (
                            <span className="border-l border-zinc-700 pl-3 truncate max-w-[150px] sm:max-w-none" title={revealedSeed || serverHash}>
                                {revealedSeed ? `Seed: ${revealedSeed.substring(0, 16)}...` : `Hash: ${serverHash.substring(0, 16)}...`}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* 2. KISIM: BAHİS VE OYUNCULAR */}
            <div className="flex flex-col gap-6 min-w-0">
                
                {/* BAHİS KUTUSU */}
                <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 shadow-lg shrink-0 w-full">
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between items-center mb-1.5">
                                <label className="text-xs text-zinc-400 font-bold uppercase tracking-wider">Bet Amount</label>
                                {/* ⚠️ Sınırlar cüzdana göre; sabit yazmak BB'de yanlış rakam gösterirdi. */}
                                <span className="text-xs text-zinc-500 mm-num">Min: <WalletValue amount={betLimits.jackpot_min} wallet={wallet} /> | Max: <WalletValue amount={betLimits.jackpot_max} wallet={wallet} /></span>
                            </div>
                            
                            <div className="flex bg-[#15171a] border border-[#2A2D31] rounded-xl overflow-hidden focus-within:border-[#B059E8] transition-colors shadow-inner">
                                <div className="pl-3 flex items-center justify-center">
                                    <span className="text-[#B059E8] font-bold">$</span>
                                </div>
                                <input
                                    type="text" 
                                    value={betAmount}
                                    onChange={(e) => setBetAmount(e.target.value)}
                                    disabled={phase === 'spinning' || isJoining}
                                    className="w-full bg-transparent py-3 px-2 text-white font-bold outline-none disabled:opacity-50 uppercase min-w-0"
                                    placeholder="100"
                                />
                                <div className="flex border-l border-[#2A2D31] shrink-0">
                                    <button onClick={() => setBetAmount(String(parseAmount(betAmount) + 100))} disabled={phase === 'spinning'} className="px-2 sm:px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">+100</button>
                                    <button onClick={() => setBetAmount(String(Math.floor(parseAmount(betAmount) * 0.5)))} disabled={phase === 'spinning'} className="px-2 sm:px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">1/2</button>
                                    <button onClick={() => setBetAmount(formatAmount(Math.floor(parseAmount(betAmount) * 2)))} disabled={phase === 'spinning'} className="px-2 sm:px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors">2x</button>
                                </div>
                            </div>
                        </div>

                        <button 
                            onClick={joinJackpot}
                            disabled={phase === 'spinning' || isJoining}
                            className={`w-full py-4 rounded-xl font-heading font-black text-lg uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2
                                ${phase === 'spinning' 
                                    ? 'bg-zinc-800 text-zinc-500 border border-zinc-700 cursor-not-allowed' 
                                    : 'bg-gradient-to-r from-[#15913f] to-[#16A34A] hover:from-[#16A34A] hover:to-[#2ecf6e] text-white shadow-[0_0_20px_rgba(22,163,74,0.3)] hover:scale-[1.02]'}`}
                        >
                            {isJoining ? 'Processing...' : phase === 'spinning' ? 'Wheel is Spinning' : 'Join Game'}
                        </button>
                    </div>
                </div>

                {/* OYUNCU LİSTESİ KUTUSU */}
                <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-lg flex-1 flex flex-col overflow-hidden min-h-[300px] w-full">
                    <div className="bg-[#2A2D31]/30 px-4 py-3 border-b border-[#2A2D31] flex justify-between items-center">
                        <div className="flex items-center gap-2 text-zinc-300 text-sm font-bold uppercase tracking-wider">
                            <Users className="w-4 h-4 text-[#c98bf0]" />
                            <span>Players ({players.length})</span>
                        </div>
                    </div>
                    
                    <div className="overflow-y-auto flex-1 p-3 space-y-2 scrollbar-thin scrollbar-thumb-zinc-700">
                        {players.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-zinc-600 text-sm">
                                <Trophy className="w-10 h-10 mb-2 opacity-20" />
                                <span>No players in the pot yet.</span>
                                <span className="text-xs mt-1">Be the first to join!</span>
                            </div>
                        ) : (
                            [...players].sort((a, b) => b.amount - a.amount).map((p, idx) => {
                                const isMe = p.user_id === user?.id;
                                const chance = ((p.amount / totalPot) * 100).toFixed(2);
                                const playerColor = SLICE_COLORS[players.findIndex(x => x.user_id === p.user_id) % SLICE_COLORS.length];

                                return (
                                    <div key={p.user_id} className={`flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all ${isMe ? 'bg-white/5 border-white/10' : 'bg-[#15171a] border-[#2A2D31] hover:border-[#2A2D31]/80'} min-w-0`}>
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {/* Çark Renk Belirteci */}
                                            <div className="w-1 h-8 rounded-full shrink-0" style={{ backgroundColor: playerColor }}></div>
                                            
                                            {/* 🛡️ DOĞRUDAN API İLE AVATAR */}
                                            <Avatar 
                                                username={p.username} 
                                                className="w-8 h-8 rounded-lg object-cover border border-[#2A2D31] shadow-md bg-[#15171a] shrink-0"
                                            />
                                            
                                            <div className="flex flex-col flex-1 min-w-0">
                                                <span className={`font-bold text-sm truncate w-full ${isMe ? 'text-[#c98bf0]' : 'text-zinc-200'}`}>
                                                    {p.username}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex flex-col items-end gap-0.5 shrink-0 ml-2">
                                            <span className="mm-num font-black text-white text-sm">
                                                <WalletValue amount={p.amount} wallet={wallet} />
                                            </span>
                                            <span className="text-xs font-bold text-zinc-500 mm-num">
                                                {chance}%
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
                
            </div>

          </main>
        </div>
      </div>
    </div>
  );
};